import { Popconfirm, Select } from 'antd';
import { ComponentProps, FC, useEffect, useRef, useState } from 'react';
import { UseControllerProps, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DonorSelect from '../DonorSelect';

type PropTypes = {
  controlled: UseControllerProps<any>;
  originalDonorId?: string;
  productsCount?: number;
  onPendingChange?: (isPending: boolean) => void;
  formEnabled?: boolean;
} & Omit<ComponentProps<typeof Select>, 'options'>;

const DonorSelectWithConfirm: FC<PropTypes> = ({
  controlled,
  originalDonorId = '',
  productsCount = 0,
  onPendingChange,
  formEnabled = false,
  ...restProps
}) => {
  const { t } = useTranslation('donation.EditDonationForm');
  const { setValue } = useFormContext();

  const [popconfirmVisible, setPopconfirmVisible] = useState(false);
  const [pendingDonorId, setPendingDonorId] = useState<string | null>(null);
  const stateRef = useRef({
    isConfirming: false,
    isInitialized: false,
    previousDonorId: originalDonorId,
  });

  const currentDonorId = useWatch({
    control: controlled.control,
    name: controlled.name,
  });

  useEffect(() => {
    if (!formEnabled) {
      setPopconfirmVisible(false);
      setPendingDonorId(null);
      stateRef.current = {
        isConfirming: false,
        isInitialized: false,
        previousDonorId: originalDonorId,
      };
      onPendingChange?.(false);
    }
  }, [formEnabled, originalDonorId, onPendingChange]);

  useEffect(() => {
    if (!formEnabled) {
      return;
    }

    if (currentDonorId && !stateRef.current.isInitialized) {
      stateRef.current.isInitialized = true;
      stateRef.current.previousDonorId = currentDonorId;
      return;
    }

    if (stateRef.current.isConfirming) {
      stateRef.current.isConfirming = false;
      stateRef.current.previousDonorId = currentDonorId;
      return;
    }

    const isDonorChanged =
      currentDonorId && currentDonorId !== stateRef.current.previousDonorId;

    if (!isDonorChanged) {
      if (currentDonorId) {
        stateRef.current.previousDonorId = currentDonorId;
      }
      return;
    }

    const shouldShowConfirm =
      currentDonorId !== originalDonorId &&
      productsCount > 0 &&
      !popconfirmVisible;

    if (shouldShowConfirm) {
      setPendingDonorId(currentDonorId);
      setPopconfirmVisible(true);
      onPendingChange?.(true);

      setValue(controlled.name, stateRef.current.previousDonorId, {
        shouldDirty: stateRef.current.previousDonorId !== originalDonorId,
        shouldValidate: false,
      });
    } else {
      stateRef.current.previousDonorId = currentDonorId;
    }
  }, [
    currentDonorId,
    originalDonorId,
    productsCount,
    popconfirmVisible,
    setValue,
    controlled.name,
    onPendingChange,
    formEnabled,
  ]);

  const handleConfirm = () => {
    if (pendingDonorId) {
      stateRef.current.isConfirming = true;
      setValue(controlled.name, pendingDonorId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setPopconfirmVisible(false);
    setPendingDonorId(null);
    onPendingChange?.(false);
  };

  const handleCancel = () => {
    setPopconfirmVisible(false);
    setPendingDonorId(null);
    onPendingChange?.(false);
  };

  return (
    <Popconfirm
      title={t('donorChangeWarning', {
        count: productsCount,
      })}
      overlayStyle={{ maxWidth: 300 }}
      visible={popconfirmVisible}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      okText={t('yes')}
      cancelText={t('no')}
      okButtonProps={{ danger: true }}
      placement="topLeft"
    >
      <div>
        <DonorSelect controlled={controlled} {...restProps} />
      </div>
    </Popconfirm>
  );
};

export default DonorSelectWithConfirm;
